import { useMemo } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { ZanbatoMarksValuationItem } from 'api';
import { EMPTY_VALUE_FOR_TABLE } from 'common/constants/valuations';
import {
  COLUMN_DATE,
  COLUMN_PRICE,
  COLUMN_SIZE,
  COLUMN_SOURCE,
  COLUMN_TITLE,
} from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/constants';
import normalizeSourceRowsOrder from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/normalizeSourceRowsOrder';
import { MutualFundMark } from 'services/hooks/companies';
import { MarksTable, MarksTableCell, MarksTableHead, MarksTableRow } from './MutualFundMarksCard.styles';
import { buildMutualFundMarksSourceRows, formatCurrency, toNum } from './MutualFundMarksCard.utils';

interface PreviewRow {
  key: string;
  title: string;
  date: string | null;
  source: string;
  size: string;
  pricePerShare: string;
}

export interface MutualFundMarksPreviewModalProps {
  open: boolean;
  onClose: () => void;
  onAddToValuation: () => void;
  zanbatoMarks: ZanbatoMarksValuationItem[];
  edgarMarks: MutualFundMark[];
  measurementDate: string;
}

const buildPreviewRows = (
  zanbatoMarks: ZanbatoMarksValuationItem[],
  edgarMarks: MutualFundMark[],
  measurementDate: string
): PreviewRow[] => {
  const sourceRows = buildMutualFundMarksSourceRows(zanbatoMarks, edgarMarks, measurementDate);
  const orderedRows = normalizeSourceRowsOrder(sourceRows);

  return orderedRows.map((row, index) => ({
    key: row.source_record_id ?? `mfm-row-${index}`,
    title: row.title,
    date: row.date ?? null,
    source: row.source ?? '',
    size: row.size ?? EMPTY_VALUE_FOR_TABLE,
    pricePerShare: formatCurrency(toNum(row.price_per_share ?? null)),
  }));
};

const MutualFundMarksPreviewModal = ({
  open,
  onClose,
  onAddToValuation,
  zanbatoMarks,
  edgarMarks,
  measurementDate,
}: MutualFundMarksPreviewModalProps) => {
  const rows = useMemo(
    () => buildPreviewRows(zanbatoMarks, edgarMarks, measurementDate),
    [zanbatoMarks, edgarMarks, measurementDate]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="mutual-fund-marks-card__preview-modal">
      <DialogTitle component="div">
        <Typography variant="h2" sx={{ fontWeight: 600 }}>
          Mutual Fund Marks Preview
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ margin: '0.5rem 0' }}>
          Last 3 months ending on {measurementDate}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ margin: '0.5rem 0' }}>
          Important: Weightings are set on the valuation page, which determines the target price per share.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {rows.length > 0 ? (
          <MarksTable>
            <thead>
              <MarksTableRow>
                <MarksTableHead>{COLUMN_TITLE}</MarksTableHead>
                <MarksTableHead>{COLUMN_DATE}</MarksTableHead>
                <MarksTableHead>{COLUMN_SOURCE}</MarksTableHead>
                <MarksTableHead>{COLUMN_SIZE}</MarksTableHead>
                <MarksTableHead>{COLUMN_PRICE}</MarksTableHead>
              </MarksTableRow>
            </thead>
            <tbody>
              {rows.map(row => (
                <MarksTableRow key={row.key}>
                  <MarksTableCell>{row.title}</MarksTableCell>
                  <MarksTableCell>{row.date ? row.date : EMPTY_VALUE_FOR_TABLE}</MarksTableCell>
                  <MarksTableCell>{row.source}</MarksTableCell>
                  <MarksTableCell>{row.size}</MarksTableCell>
                  <MarksTableCell>{row.pricePerShare}</MarksTableCell>
                </MarksTableRow>
              ))}
            </tbody>
          </MarksTable>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            No mutual fund marks data available for this measurement date.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="mutual-fund-marks-card__preview-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onAddToValuation}
          disabled={rows.length === 0}
          data-testid="mutual-fund-marks-card__preview-add">
          Add to Valuation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MutualFundMarksPreviewModal;
